import { QueryClient } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  Outlet,
  useNavigate,
} from "@tanstack/react-router";
import { useEffect } from "react";
import { CommonRoute } from "@/constants/routes";
import { validateToken } from "@/lib/tokenValidator";
import { UserAuthUtil } from "@/utils/userAuth";
import { ID_REQUEST_NAME_STORAGE } from "@/constants/commons";
import { useAuth } from "@/context/AuthContext";
import { getIdRequest } from "@/utils/commoms";
import { InteractionStatus } from "@azure/msal-browser";
import { UserDropdown } from "@/shared/components/user-dropdown/UserDropdown";
import { useUserAccessControl } from "@/context/UserAccessControl";
import { clearCurrentIdRoleToSessionStorage } from "@/utils/storage";

interface RouterContext {
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootComponent,
});

function RootComponent() {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, logout, instance, inProgress } = useAuth();
  const { roleList, updateRole } = useUserAccessControl();

  const idRequest = getIdRequest();

  const onGoHome = () => {
    console.log("ir al inicio");
    clearCurrentIdRoleToSessionStorage();
    updateRole({
      segcenObject: "",
      segcenAction: "",
      description: "",
      id: 0
    });
    navigate({ to: CommonRoute.ROLES });
  }

  useEffect(() => {
    if (!isLoading) {
      loadAccessPage();
    }
  }, [isAuthenticated, isLoading]);

  /**
   * @description: se valida que la interaccion sea HandleRedirect,
   * para que entre a obtener la nueva respuesta del logueo, ya que ne algunas 
   * ocasiones, cuando se usa navigate para ir al login o retry, se renderiza nuevamente y 
   * vuelve a entrar responseAccessRedirect del anterior logueo.
   */
  const loadAccessPage = async () => {
    const currentPath = window.location.pathname;

    const responseAccessRedirect = await instance.handleRedirectPromise();
    if (responseAccessRedirect && inProgress === InteractionStatus.HandleRedirect) {
      localStorage.setItem(
        import.meta.env.VITE_APP_AZURE_AUTH_RESULT || "portal-auth-result",
        JSON.stringify(responseAccessRedirect)
      );
      return;
    }

    if (currentPath === CommonRoute.LOGIN || currentPath === CommonRoute.RETRY) {
      return;
    }

    const tokenValidation = validateToken();
    sessionStorage.setItem(ID_REQUEST_NAME_STORAGE, String(idRequest));
    if (!tokenValidation.exists) {
      navigate({ to: CommonRoute.LOGIN });
      return;
    }

    if (tokenValidation.isExpired) {
      navigate({ to: CommonRoute.RETRY });
      return;
    }

    if (!isAuthenticated) {
      navigate({ to: CommonRoute.LOGIN });
      return;
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col h-screen items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Verificando autenticación...</p>
        </div>
      </div>
    );
  }

  const fullName = UserAuthUtil.getFullName();

  const logoutSession = async () => {
    sessionStorage.clear();
    sessionStorage.setItem(ID_REQUEST_NAME_STORAGE, String(idRequest));
    await logout();
  }

  return (
    <nav className="bg-white border-brand-grayscale-200">
      <div className="mx-auto px-4 sm:px-6 lg:px-8 border-b-[#B8C3C8] border-solid border-[1px]">
        <div className="flex flex-wrap justify-center sm:justify-between items-center h-22 md:h-16">
          <div className="flex items-center">
            <h1 className="text-xl font-foco font-bold text-brand-blue-light-600">
              Pacifico
            </h1>
          </div>

          {fullName && (
            <div className="flex flex-wrap items-center space-x-4 w-full sm:w-auto justify-center sm:justify-end mt-4 sm:mt-0">
              <UserDropdown userName={fullName} onLogout={logoutSession} isOneMoreRole={roleList.length > 1} onGoHome={onGoHome} />
            </div>
          )}
        </div>
      </div>
      <main className="mx-auto">
        <Outlet />
      </main>
    </nav>
  );
}
